import {exhaustive} from '@freckle/exhaustive'
import {maybe, mthen} from '@freckle/maybe'
import {
  formatPoint,
  formatPoints,
} from '@freckle/educator-entities/ts/math/common/helpers/question-formatting'
import type {PointT} from '@freckle/educator-entities/ts/math/common/logic/equation'
import {
  generateSamplePointsForLinearEquation,
  generateSamplePointsForExponentialEquation,
  generateSamplePointsForQuadraticEquation,
} from '@freckle/educator-entities/ts/math/common/logic/equation'
import type {MathQuestionGraphExponentialMetadataAttrs} from '@freckle/educator-entities/ts/math/common/models/questions/question-graph-exponential'
import type {MathQuestionGraphLinearMetadataAttrs} from '@freckle/educator-entities/ts/math/common/models/questions/question-graph-linear'
import type {MathQuestionGraphLinearInequalityMetadataAttrs} from '@freckle/educator-entities/ts/math/common/models/questions/question-graph-linear-inequality'
import {formatInequality} from '@freckle/educator-entities/ts/math/common/models/questions/question-graph-linear-inequality'
import type {MathQuestionGraphQuadraticMetadataAttrs} from '@freckle/educator-entities/ts/math/common/models/questions/question-graph-quadratic'
import type {MathQuestionGraphScatterPointsMetadataAttrs} from '@freckle/educator-entities/ts/math/common/models/questions/question-graph-scatter-points'

export type GraphQuestionMetadataAttrs =
  | {
      answerType: 'graph_exponential'
      content: MathQuestionGraphExponentialMetadataAttrs
    }
  | {
      answerType: 'graph_linear_inequality'
      content: MathQuestionGraphLinearInequalityMetadataAttrs
    }
  | {
      answerType: 'graph_linear'
      content: MathQuestionGraphLinearMetadataAttrs
    }
  | {
      answerType: 'graph_quadratic'
      content: MathQuestionGraphQuadraticMetadataAttrs
    }
  | {
      answerType: 'graph_scatter_points'
      content: MathQuestionGraphScatterPointsMetadataAttrs
    }

export type GraphCorrectAnswerFormatT = 'text' | 'list'

type Props = {
  metadata: GraphQuestionMetadataAttrs
  format: GraphCorrectAnswerFormatT
}

export const GraphAnswer = (props: Props) => {
  const {metadata, format} = props

  const mPoints = generateSamplePoints(metadata)
  const mPrefix = getPrefix(metadata)

  const separator = '\n'

  if (mPoints === null || mPoints === undefined) {
    // eslint-disable-next-line i18next/no-literal-string
    return <>{'No sample points found'}</>
  }

  switch (format) {
    case 'text':
      const formatted = maybe(
        () => `${formatPoints(mPoints, separator)}${separator}...`,
        prefix => `${prefix}${separator}${formatPoints(mPoints, separator)}${separator}...`,
        mPrefix,
      )
      return <span>{formatted}</span>
    case 'list':
      const prefixItem = mthen(mPrefix, prefix => <li key="prefix">{prefix}</li>)
      const items = mPoints.map((p, i) => <li key={i}>{formatPoint(p)}</li>)
      return (
        <ul>
          {prefixItem}
          {items}
        </ul>
      )
    default:
      return exhaustive(format)
  }
}

const generateSamplePoints = (
  metadata: GraphQuestionMetadataAttrs,
): Array<PointT> | undefined | null => {
  switch (metadata.answerType) {
    case 'graph_linear':
      return generateSamplePointsForLinearEquation(
        {
          start: metadata.content.graphGridLowerBound.x,
          stop: metadata.content.graphGridUpperBound.x,
          stepBy: metadata.content.graphGridStepX,
        },
        metadata.content.graphLinearEquationCoefficients,
      )
    case 'graph_exponential':
      return generateSamplePointsForExponentialEquation(
        {
          start: metadata.content.graphGridLowerBound.x,
          stop: metadata.content.graphGridUpperBound.x,
          stepBy: metadata.content.graphGridStepX,
        },
        metadata.content.graphExponentialEquationCoefficients,
      )
    case 'graph_quadratic':
      const {vertex, points} = generateSamplePointsForQuadraticEquation(
        {
          start: metadata.content.graphGridLowerBound.x,
          stop: metadata.content.graphGridUpperBound.x,
          stepBy: metadata.content.graphGridStepX,
        },
        metadata.content.graphQuadraticEquationCoefficients,
      )
      return [vertex].concat(points)
    case 'graph_linear_inequality':
      return generateSamplePointsForLinearEquation(
        {
          start: metadata.content.graphGridLowerBound.x,
          stop: metadata.content.graphGridUpperBound.x,
          stepBy: metadata.content.graphGridStepX,
        },
        metadata.content.graphLinearInequalityEquationCoefficients,
      )
    case 'graph_scatter_points':
      return metadata.content.graphScatterPointsPoints
    default:
      return exhaustive(metadata)
  }
}

const getPrefix = (metadata: GraphQuestionMetadataAttrs): string | null => {
  switch (metadata.answerType) {
    case 'graph_linear_inequality':
      return formatInequality(metadata.content.graphLinearInequalityInequality)
    default:
      return null
  }
}
